import { API_ENDPOINTS } from '@/api/endpoints.constant';
import { AppConstants } from '@/app.constants';
import { systemConfig } from '@/config/systemConfig';
import { login } from '@/store/slices/authSlice';
import { store } from '@/store/store';
import { clivApiRequest } from '@/utils/apiClient';
import { ClivLoginResponse } from '@/types/ClivLoginResponse.type';
import { resumePendingUploads } from '@/serviceworker/resumePendingUploads.util';

const AUTH_LOG = '[inspect-auth]';

/**
 * If the browser has a valid SSO cookie from another app, exchange it for an inspect session.
 *
 * @see ../../../backend-v2/docs/README-SSO.md
 */
export async function tryInspectSsoSilentLogin(): Promise<void> {
  if (store.getState().auth.token) {
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(AUTH_LOG, 'tryInspectSsoSilentLogin: skip — already have a token');
    return;
  }
  const b2cIdToken = new URLSearchParams(window.location.hash).get('#id_token');
  if (b2cIdToken && b2cIdToken.length > 0) {
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(AUTH_LOG, 'tryInspectSsoSilentLogin: skip — B2C id_token present in hash');
    return;
  }
  try {
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(
      AUTH_LOG,
      'tryInspectSsoSilentLogin: POST /auth/login (credentials: include, no B2C token)',
      {
        tenantId: systemConfig.tenantId,
        appName: AppConstants.APP_NAME,
        apiUrl: systemConfig.apiUrl,
      }
    );
    const url = `${systemConfig.apiUrl}/${API_ENDPOINTS.AUTH.LOGIN}`;
    const response = await clivApiRequest<ClivLoginResponse>(
      {
        url,
        method: 'POST',
        body: {
          tenantId: systemConfig.tenantId,
          appName: AppConstants.APP_NAME,
        },
      },
      true
    );
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(AUTH_LOG, 'tryInspectSsoSilentLogin: response', {
      ok: response.ok,
      status: response.status,
      requestId: response.requestId,
      hasAccessToken: Boolean(response.data?.accessToken),
      hasUser: Boolean(response.data?.user),
    });
    if (response.ok && response.data?.accessToken && response.data.user) {
      // eslint-disable-next-line no-console -- login diagnostics
      console.debug(AUTH_LOG, 'tryInspectSsoSilentLogin: dispatch login()', {
        userId: response.data.user.id,
        email: response.data.user.email,
      });
      store.dispatch(
        login({
          token: response.data.accessToken,
          user: response.data.user,
        })
      );
      void resumePendingUploads();
    } else {
      // eslint-disable-next-line no-console -- login diagnostics
      console.debug(
        AUTH_LOG,
        'tryInspectSsoSilentLogin: no session (SSO cookie missing, invalid, or user not allowed)'
      );
    }
  } catch (error) {
    // eslint-disable-next-line no-console -- login diagnostics
    console.debug(AUTH_LOG, 'tryInspectSsoSilentLogin: silent SSO attempt failed', error);
  }
}
